import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const VerificationQueue = () => {
  const [draggedItem, setDraggedItem] = useState(null);

  const verificationStages = [
  {
    id: 'pending',
    title: 'Pending Review',
    count: 12,
    color: 'bg-amber-100 border-amber-200',
    headerColor: 'bg-amber-50'
  },
  {
    id: 'documents',
    title: 'Document Verification',
    count: 8,
    color: 'bg-blue-100 border-blue-200',
    headerColor: 'bg-blue-50'
  },
  {
    id: 'background',
    title: 'Background Check',
    count: 5,
    color: 'bg-purple-100 border-purple-200',
    headerColor: 'bg-purple-50'
  },
  {
    id: 'approved',
    title: 'Approved',
    count: 23,
    color: 'bg-emerald-100 border-emerald-200',
    headerColor: 'bg-emerald-50'
  }];


  const partners = [
  {
    id: 1,
    name: "Sarah Johnson",
    businessName: "Johnson\'s Catering Services",
    vertical: "Food",
    stage: "pending",
    avatar: "https://images.unsplash.com/photo-1684262855358-88f296a2cfc2",
    avatarAlt: "Professional headshot of woman with brown hair in white blazer smiling",
    submittedAt: "2025-10-27T10:30:00Z",
    documents: ["Business License", "Food Safety Certificate"],
    priority: "high"
  },
  {
    id: 2,
    name: "Michael Chen",
    businessName: "Chen Home Services",
    vertical: "Services",
    stage: "documents",
    avatar: "https://images.unsplash.com/photo-1659323593305-ad05297db6a2",
    avatarAlt: "Professional headshot of Asian man in navy suit with confident smile",
    submittedAt: "2025-10-26T14:20:00Z",
    documents: ["ID Verification", "Insurance Certificate"],
    priority: "medium"
  },
  {
    id: 3,
    name: "Emma Rodriguez",
    businessName: "Rodriguez Property Management",
    vertical: "Property",
    stage: "background",
    avatar: "https://images.unsplash.com/photo-1563220599-bc8cb877de5d",
    avatarAlt: "Professional headshot of Hispanic woman with long dark hair in business attire",
    submittedAt: "2025-10-25T09:15:00Z",
    documents: ["Real Estate License", "Background Check"],
    priority: "high"
  },
  {
    id: 4,
    name: "David Thompson",
    businessName: "Adventure Tours Co.",
    vertical: "Tours",
    stage: "approved",
    avatar: "https://images.unsplash.com/photo-1598226863630-2826f1d31532",
    avatarAlt: "Professional headshot of man with beard in casual blue shirt outdoors",
    submittedAt: "2025-10-24T16:45:00Z",
    documents: ["Tour Operator License", "Insurance"],
    priority: "low"
  }];


  const getPartnersByStage = (stageId) => {
    return partners?.filter((partner) => partner?.stage === stageId);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':return 'bg-red-100 text-red-800';
      case 'medium':return 'bg-amber-100 text-amber-800';
      case 'low':return 'bg-green-100 text-green-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerticalColor = (vertical) => {
    switch (vertical) {
      case 'Food':return 'bg-orange-100 text-orange-800';
      case 'Services':return 'bg-blue-100 text-blue-800';
      case 'Property':return 'bg-purple-100 text-purple-800';
      case 'Tours':return 'bg-green-100 text-green-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const submitted = new Date(dateString);
    const diffInHours = Math.floor((now - submitted) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const handleDragStart = (e, partner) => {
    setDraggedItem(partner);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e?.preventDefault();
    if (draggedItem && draggedItem?.stage !== targetStage) {
      // In a real app, this would update the backend
      console.log(`Moving ${draggedItem?.name} to ${targetStage}`);
    }
    setDraggedItem(null);
  };

  return (
    <div className="space-y-6">
      {/* Queue Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {verificationStages?.map((stage) =>
        <div key={stage?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{stage?.title}</p>
                <p className="text-2xl font-bold text-foreground">{stage?.count}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg ${stage?.headerColor} flex items-center justify-center`}>
                <Icon name="Users" size={24} className="text-primary" />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {verificationStages?.map((stage) =>
        <div
          key={stage?.id}
          className={`${stage?.color} rounded-lg p-4 min-h-96`}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, stage?.id)}>

            <div className={`${stage?.headerColor} rounded-lg p-3 mb-4`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">{stage?.title}</h3>
                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                  {getPartnersByStage(stage?.id)?.length}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {getPartnersByStage(stage?.id)?.map((partner) =>
            <div
              key={partner?.id}
              draggable
              onDragStart={(e) => handleDragStart(e, partner)}
              className="bg-card border border-border rounded-lg p-4 cursor-move hover:elevation-2 transition-all">

                  <div className="flex items-start space-x-3">
                    <Image
                  src={partner?.avatar}
                  alt={partner?.avatarAlt}
                  className="w-10 h-10 rounded-full object-cover" />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-foreground truncate">{partner?.name}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(partner?.priority)}`}>
                          {partner?.priority}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2 truncate">
                        {partner?.businessName}
                      </p>
                      
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${getVerticalColor(partner?.vertical)}`}>
                          {partner?.vertical}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatTimeAgo(partner?.submittedAt)}
                        </span>
                      </div>

                      <div className="mb-3">
                        <p className="text-xs text-muted-foreground mb-1">Documents:</p>
                        <div className="flex flex-wrap gap-1">
                          {partner?.documents?.map((doc, index) =>
                      <span key={index} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                              {doc}
                            </span>
                      )}
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button size="xs" variant="outline" className="flex-1">
                          <Icon name="Eye" size={14} className="mr-1" />
                          Review
                        </Button>
                        <Button size="xs" variant="ghost">
                          <Icon name="MessageSquare" size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>);

};

export default VerificationQueue;